// The CRE page's maps of the two centres its benchmark examples come from: each centre's OSM
// outlines and tenant stores (inline JSON, IndustriesHelper#cre_centre_maps) in its chart colour
// on the trends maps' basemap, read-only like them. Both maps keep one zoom, the one that fits the
// larger centre, so the centres compare at a glance; a store's dot grows with the square root of
// its panel transactions, and hovering it names the brand. MapLibre is import()ed when the maps
// near the viewport, as on the trends pages.
import { whenVisible } from "./lazy"
import { STYLE_URL } from "./trends_map"
import { createTip } from "./tooltip"

const PADDING = 24

export function initCentreMaps() {
  document.querySelectorAll("[data-centre-maps]").forEach((el) => {
    whenVisible(el, () => mount(el).catch(() => {}))
  })
}

async function mount(el) {
  const { default: maplibregl } = await import("maplibre-gl")
  const { centres } = JSON.parse(el.querySelector("script[type='application/json']").textContent)
  const maps = await Promise.all(centres.map((c, i) => mountMap(maplibregl, el.querySelector(`[data-centre-map="${i}"]`), c)))

  const frame = () => {
    const cameras = maps.map(({ map, bounds }) => map.cameraForBounds(bounds, { padding: PADDING }))
    if (cameras.some((c) => !c)) return

    const zoom = Math.min(...cameras.map((c) => c.zoom))
    maps.forEach(({ map }, i) => map.jumpTo({ center: cameras[i].center, zoom }))
  }
  frame()
  maps.forEach(({ map }) => map.on("resize", frame))
}

function mountMap(maplibregl, el, centre) {
  const points = centre.outlines.flat()
  const lons = points.map((p) => p[0])
  const lats = points.map((p) => p[1])
  const bounds = [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]]
  const map = new maplibregl.Map({
    container: el,
    style: STYLE_URL,
    interactive: false,
    attributionControl: false,
    bounds,
    fitBoundsOptions: { padding: PADDING }
  })
  // OSM basemap and outlines on a public page: the compact control carries the tiles' credit. The
  // scale sits top left: on a phone the credit, expanded until the map is touched, spans the bottom.
  map.addControl(new maplibregl.AttributionControl({ compact: true }))
  map.addControl(new maplibregl.ScaleControl({ maxWidth: 90, unit: "metric" }), "top-left")

  return new Promise((resolve) => {
    map.on("load", () => {
      addLayers(map, centre)
      addTooltip(el, map)
      resolve({ map, bounds })
    })
  })
}

function addLayers(map, { color, outlines, stores }) {
  map.addSource("outlines", {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: outlines.map((ring) => ({ type: "Feature", geometry: { type: "Polygon", coordinates: [ring] }, properties: {} }))
    }
  })
  map.addLayer({ id: "outline-fill", type: "fill", source: "outlines", paint: { "fill-color": color, "fill-opacity": 0.1 } })
  map.addLayer({ id: "outline-line", type: "line", source: "outlines", paint: { "line-color": color, "line-width": 1.5 } })

  map.addSource("stores", {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: stores.map(([lon, lat, n, name]) => ({
        type: "Feature", geometry: { type: "Point", coordinates: [lon, lat] }, properties: { n, name }
      }))
    }
  })
  map.addLayer({
    id: "store-dots",
    type: "circle",
    source: "stores",
    // The busiest stores underneath, so the small ones stay visible on top of them.
    layout: { "circle-sort-key": ["-", 0, ["get", "n"]] },
    paint: {
      "circle-radius": ["interpolate", ["linear"], ["sqrt", ["get", "n"]], 1, 2.5, 100, 9],
      "circle-color": color,
      "circle-stroke-color": "#ffffff",
      "circle-stroke-width": 0.8,
      "circle-opacity": 0.85
    }
  })
}

// The trends maps' hover pill, naming the store's brand.
function addTooltip(el, map) {
  const tip = createTip(el)
  map.on("mousemove", "store-dots", (e) => {
    const name = e.features && e.features[0] && e.features[0].properties.name
    if (name) {
      tip.show(e.point.x, e.point.y, name)
    } else {
      tip.hide()
    }
  })
  map.on("mouseleave", "store-dots", () => tip.hide())
}
